
import { computed, defineComponent, shallowRef } from 'vue';
import { useStore } from 'vuex';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import JWTUtils from '@/utils/JWTUtils';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import HostExchangeManager from '@/manager/3.6/HostExchangeManager';
import { userSettingsStore } from '@/store';
import _ from 'lodash';
import BootstrapHelper from '@/utils/bootstrapHelpers';
import ModalConfirmation from '@/components/modals/Confirmation.vue';
import ProductFeaturesUtils from '@/utils/ProductFeaturesUtils';
import { makeGettersWatchable } from '@/utils/StoreWatch';

export default defineComponent({
    components: {
        ModalConfirmation,
    },
    setup() {
        const store = useStore();
        const userSettings = makeGettersWatchable(userSettingsStore);

        const accessToken = theLocalStorageUtils.get(LocalStorageKeys.qsAccessToken);

        const customerName = `${JWTUtils.getValue(accessToken ?? '', 'fn')} ${JWTUtils.getValue(accessToken ?? '', 'ln')}`; 
        const customerEmail = `${JWTUtils.getValue(accessToken ?? '', 'un')}`; 
        const customerNumber = `${JWTUtils.getValue(accessToken ?? '', 'cn')}`; 

        var zoho = `var $zoho = $zoho || {};
             $zoho.salesiq = $zoho.salesiq || 
            {
                widgetcode: "f4a71e16409c945aae0eeea117a06f5ba6ef10981537e71359340575ab308730dfc2060e3cba99dc8bd26ff164d86899", 
                values: {}, 
                ready: function () { 
                    $zoho.salesiq.floatbutton.coin.hidetooltip();
                    $zoho.salesiq.visitor.contactnumber('${customerNumber}');
                    $zoho.salesiq.visitor.email('${customerEmail}'); 
                    $zoho.salesiq.visitor.name('${customerName}');
                    $zoho.salesiq.visitor.info({
                        "Customer Number" : '${customerNumber}'
                    });
                }
            }; 

            var d = document; 
            s = d.createElement("script"); 
            s.type = "text/javascript";
            s.id = "zsiqscript";
            s.defer = true;
            s.src = "https://salesiq.zoho.com/widget"; 
            document.body.appendChild(s);
            document.body.appendChild(
                (function ()
                {
                    var div = document.createElement('div');
                    div.id = 'zsiqwidget';
                    return div;
                })()
            );`;

        const isChatEnabled = ProductFeaturesUtils.isChatEnabled();
        if (isChatEnabled) {
            let script = document.createElement('script');
            script.type = 'text/javascript';
            script.text = zoho;
            document.head.appendChild(script);
        }

        return {
            store,
            userSettings,
            isChatEnabled,
        };
    },
    data() {
        return {
            guideStepsCompleted: 0,
            guideStepsTotal: 0,
            guideIsVisible: true,
            modalConfirmation: {
                show: false,
                component: shallowRef(ModalConfirmation),
                message: '',
                buttons: [] as any,
            },
        };
    },
    mounted() {
        const computedProductGuideSteps = computed(() => this.userSettings.ProductGuideSteps);
        const productGuideSteps = _.cloneDeep(computedProductGuideSteps.value);
        this.guideStepsCompleted = productGuideSteps.filter((x: { completed: boolean }) => x.completed === true).length;
        this.guideStepsTotal = productGuideSteps.length - 1;

        // Ensure that guideStepsCompleted does not exceed guideStepsTotal
        this.guideStepsCompleted = (this.guideStepsCompleted > this.guideStepsTotal) ? this.guideStepsTotal : this.guideStepsCompleted;

        const result = computed(() => this.userSettings.DisplayProductGuide);
        this.guideIsVisible = result.value;

        this.initializeGoogleAnalytics();
    },
    created() {
        this.userSettings.watch(
            'ProductGuideSteps',
            async () => {
                const computedProductGuideSteps = computed(() => this.userSettings.ProductGuideSteps);
                const productGuideSteps = _.cloneDeep(computedProductGuideSteps.value);
                this.guideStepsCompleted = productGuideSteps.filter((x: { completed: boolean }) => x.completed === true).length;
                this.guideStepsTotal = productGuideSteps.length - 1;

                // Ensure that guideStepsCompleted does not exceed guideStepsTotal
                this.guideStepsCompleted = (this.guideStepsCompleted > this.guideStepsTotal) ? this.guideStepsTotal : this.guideStepsCompleted;
            },
            { deep: true }
        );

        this.userSettings.watch('DisplayProductGuide', async () => {
            const result = computed(() => this.userSettings.DisplayProductGuide);
            this.guideIsVisible = result.value;
            BootstrapHelper.tooltipInitialize();
        });
    },
    updated() {
        BootstrapHelper.tooltipInitialize();
    },
    methods: {
        chatEnabled(isEnabled: boolean): string {
            return isEnabled ? 'chat-enabled' : 'chat-disabled';
        },
        async saveAndClose() {
            (this as any).eventBus.emit('saveAndClose');
        },
        async closeApp() {
            this.modalConfirmation.show = true;
            this.modalConfirmation.message = `Are you sure you want to close?  Any changes made will be lost.`;
            this.modalConfirmation.buttons = [
                {
                    label: 'Yes',
                    css: 'btn-primary',
                    callback: async () => {
                        await HostExchangeManager.closeApp(false);
                    },
                },
            ];
        },
        openGuide(isvisible: boolean) {
            this.guideIsVisible = isvisible ? false : true;
            (this as any).eventBus.emit('toggleGuide', {
                isvisible: this.guideIsVisible,
            });
        },
        initializeGoogleAnalytics() {
            let noscript = document.createElement('noscript');

            let iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            iframe.height = '0';
            iframe.width = '0';
            iframe.src = `https://www.googletagmanager.com/ns.html?id=GTM-2M2V`;

            noscript.appendChild(iframe);
            document.head.appendChild(noscript);
        },
    },
});
